import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { type AccountPathType, usesTransactionCursor } from '../account-path-type';
import type {
  AccountTransactionRow,
  GetAccountTransactionsOptions,
} from '../account-transactions.model';
import { TRANSACTION_FETCH_LIMIT } from '../account-transactions.model';
import { ACCOUNTS_API } from '../accounts-api.token';

/** Safety cap so a stuck AIM cursor cannot drain forever. */
export const TRANSACTION_DRAIN_MAX_PAGES = 40;

interface TransactionsLoadArgs {
  guid: string;
  pathType: AccountPathType;
  id: string;
  query: GetAccountTransactionsOptions;
}

interface DrainCursor {
  offset: number;
  lastTransactionId?: number | string;
}

@Injectable()
export class AccountTransactionsStore {
  private readonly _api = inject(ACCOUNTS_API);

  private readonly _rows = signal<AccountTransactionRow[]>([]);
  private readonly _loading = signal(false);
  private readonly _loadingMore = signal(false);
  private readonly _error = signal<unknown | null>(null);
  private readonly _moreError = signal<unknown | null>(null);
  private readonly _truncated = signal(false);

  readonly rows = this._rows.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loadingMore = this._loadingMore.asReadonly();
  readonly error = this._error.asReadonly();
  readonly moreError = this._moreError.asReadonly();
  readonly truncated = this._truncated.asReadonly();

  private _generation = 0;
  private _last: TransactionsLoadArgs | null = null;
  private _resume: (TransactionsLoadArgs & DrainCursor) | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.reset());
  }

  async load(
    guid: string,
    pathType: AccountPathType,
    id: string,
    query: GetAccountTransactionsOptions = {}
  ): Promise<void> {
    const generation = ++this._generation;
    this._last = { guid, pathType, id, query };
    this._resume = null;
    this._truncated.set(false);
    this._loadingMore.set(false);
    this._moreError.set(null);
    this._loading.set(true);
    this._error.set(null);
    try {
      const result = await this._drain(
        { guid, pathType, id, query },
        { offset: query.offset ?? 0 },
        generation
      );
      if (generation !== this._generation) {
        return;
      }
      this._rows.set(result.rows);
      this._truncated.set(result.truncated);
      this._resume = result.resume;
    } catch (err) {
      if (generation !== this._generation) {
        return;
      }
      console.error('[accounts] transactions load failed');
      this._error.set(err);
      this._rows.set([]);
      this._truncated.set(false);
      this._resume = null;
    } finally {
      if (generation === this._generation) {
        this._loading.set(false);
      }
    }
  }

  async loadMore(): Promise<void> {
    const resume = this._resume;
    if (!resume || this._loading() || this._loadingMore()) {
      return;
    }
    const generation = this._generation;
    this._loadingMore.set(true);
    this._moreError.set(null);
    try {
      const result = await this._drain(
        resume,
        { offset: resume.offset, lastTransactionId: resume.lastTransactionId },
        generation
      );
      if (generation !== this._generation) {
        return;
      }
      this._rows.update((rows) => [...rows, ...result.rows]);
      this._truncated.set(result.truncated);
      this._resume = result.resume;
    } catch (err) {
      if (generation !== this._generation) {
        return;
      }
      console.error('[accounts] transactions load more failed');
      this._moreError.set(err);
    } finally {
      if (generation === this._generation) {
        this._loadingMore.set(false);
      }
    }
  }

  reset(): void {
    this._generation += 1;
    this._last = null;
    this._resume = null;
    this._rows.set([]);
    this._error.set(null);
    this._moreError.set(null);
    this._truncated.set(false);
    this._loading.set(false);
    this._loadingMore.set(false);
  }

  retry(): Promise<void> {
    if (!this._last) {
      return Promise.resolve();
    }
    return this.load(this._last.guid, this._last.pathType, this._last.id, this._last.query);
  }

  private async _drain(
    args: TransactionsLoadArgs,
    cursor: DrainCursor,
    generation: number
  ): Promise<{
    rows: AccountTransactionRow[];
    truncated: boolean;
    resume: (TransactionsLoadArgs & DrainCursor) | null;
  }> {
    const collected: AccountTransactionRow[] = [];
    let offset = cursor.offset;
    let lastTransactionId = cursor.lastTransactionId;
    const useCursor = usesTransactionCursor(args.pathType);
    let exhausted = false;

    for (let page = 0; page < TRANSACTION_DRAIN_MAX_PAGES; page += 1) {
      if (generation !== this._generation) {
        return { rows: collected, truncated: false, resume: null };
      }
      const payload = await firstValueFrom(
        this._api.getAccountTransactions(args.guid, args.pathType, args.id, {
          ...args.query,
          offset,
          limit: args.query.limit ?? TRANSACTION_FETCH_LIMIT,
          lastTransactionId: useCursor ? lastTransactionId : undefined,
        })
      );
      if (generation !== this._generation) {
        return { rows: collected, truncated: false, resume: null };
      }
      collected.push(...payload.transactions);
      if (payload.resultSet.allFetched) {
        exhausted = true;
        break;
      }
      const nextOffset = payload.resultSet.offset;
      const nextId = payload.resultSet.lastTransactionId;
      if (useCursor) {
        if (
          nextId === undefined ||
          (lastTransactionId !== undefined && String(nextId) === String(lastTransactionId))
        ) {
          throw new Error('AIM transaction response did not provide an advancing cursor');
        }
        lastTransactionId = nextId;
      }
      if (typeof nextOffset !== 'number' || nextOffset <= offset) {
        throw new Error('AIM transaction response did not provide an advancing offset');
      }
      offset = nextOffset;
    }

    const truncated = !exhausted;
    return {
      rows: collected,
      truncated,
      resume: truncated
        ? { ...args, offset, lastTransactionId }
        : null,
    };
  }
}
